import { useEffect, useState, useRef } from "react";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { api } from "@/services/api";

interface UserInitializerProps {
  onComplete?: () => void;
}

export function UserInitializer({ onComplete }: UserInitializerProps) {
  const [status, setStatus] = useState<
    "checking" | "initializing" | "complete" | "error"
  >("checking");
  const [message, setMessage] = useState("Checking account status...");
  const [retryCount, setRetryCount] = useState(0);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const initializeUser = async () => {
      try {
        console.log("User Initializer: Starting initialization...");
        setStatus("checking");
        setMessage("Checking account status...");

        // Check if user is already initialized
        const checkResponse = await api.isUserInitialized();
        console.log("User Initializer: Check response:", checkResponse);

        if (checkResponse.success && checkResponse.data?.initialized) {
          console.log("User Initializer: User already initialized");
          setStatus("complete");
          setMessage("Welcome back!");
          setTimeout(() => {
            onComplete?.();
          }, 1000);
          return;
        }

        // User needs initialization
        console.log("User Initializer: Starting user initialization...");
        setStatus("initializing");
        setMessage("Setting up your environment for the first time...");

        const initResponse = await api.initializeUser();
        console.log("User Initializer: Init response:", initResponse);

        if (initResponse.success) {
          console.log("User Initializer: Initialization successful");
          setStatus("complete");
          setMessage("Environment setup complete!");
          setTimeout(() => {
            onComplete?.();
          }, 1500);
        } else {
          console.log(
            "User Initializer: Initialization failed:",
            initResponse.message
          );
          setStatus("error");
          setMessage(
            initResponse.message || "Setup failed. Please refresh the page."
          );
        }
      } catch (error: any) {
        console.error("User Initializer: Error during initialization:", error);

        if (error.response?.status === 404) {
          console.log(
            "User Initializer: Endpoint not found, assuming initialized"
          );
          setStatus("complete");
          setMessage("Welcome!");
          setTimeout(() => {
            onComplete?.();
          }, 1000);
        } else if (retryCount < 2) {
          console.log(`UserInitializer: Retrying (${retryCount + 1}/3)...`);
          setRetryCount((prev) => prev + 1);
          setMessage("Retrying setup...");
          setTimeout(() => {
            hasStarted.current = false;
            setStatus("checking");
          }, 2000);
        } else {
          console.error("User Initializer: Max retries exceeded");
          setStatus("error");
          setMessage(
            "Setup failed after multiple attempts. Please refresh the page."
          );
        }
      }
    };

    initializeUser();
  }, [onComplete, retryCount]);

  return (
    <div className="fixed inset-0 bg-white/90 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
        <div className="flex flex-col items-center space-y-4">
          {status === "checking" || status === "initializing" ? (
            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          ) : status === "complete" ? (
            <CheckCircle className="h-12 w-12 text-green-600" />
          ) : (
            <AlertCircle className="h-12 w-12 text-red-600" />
          )}

          <h2 className="text-xl font-semibold text-gray-900">
            {status === "initializing"
              ? "First Time Setup"
              : status === "complete"
              ? "Ready!"
              : status === "error"
              ? "Setup Error"
              : "Preparing Your Account"}
          </h2>

          <p className="text-gray-600 text-center">{message}</p>

          {status === "initializing" && (
            <div className="mt-4 space-y-2 text-sm text-gray-500">
              <p>• Creating secure SFTP access</p>
              <p>• Setting up document storage</p>
              <p>• Configuring EDI processing</p>
              <h3 className="text-lg font-semibold text-gray-900">
                • Adding $10 credit to get you started!
              </h3>
            </div>
          )}

          {status === "error" && retryCount > 0 && (
            <div className="mt-4 text-sm text-gray-500">
              <p>
                Attempted {retryCount} time{retryCount > 1 ? "s" : ""}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
